import { useEffect, useRef } from 'react';

// How far a touch moves before it counts as a drag rather than a tap
const DRAG_START_DISTANCE = 8;
// How far, or how fast in px per ms, a drag must go before releasing it closes rather than springs back
const CLOSE_DISTANCE = 120;
const CLOSE_VELOCITY = 0.5;
const SETTLE_DURATION = 200;

const isScrolledBelowTop = (target: EventTarget | null, element: HTMLElement) => {
  let current = target instanceof Element ? target : null;
  while (current && current !== element) {
    if (current.scrollTop > 0) return true;
    current = current.parentElement;
  }
  return false;
};

// Lets diners drag a fullscreen sheet down to close it, like a native bottom sheet. A drag only starts while the
// content under the finger is scrolled to the top, so swiping down elsewhere still scrolls.
export const useSwipeDownToClose = (element: HTMLElement | null, onClose: () => void) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!element) return undefined;

    let start: { time: number, x: number, y: number } | null = null;
    let dragging = false;
    let distance = 0;
    let closeTimeout: number | undefined;

    const { style } = element;
    const moveTo = (offset: string, animate: boolean) => {
      style.transition = animate ? `transform ${SETTLE_DURATION}ms ease-out` : 'none';
      style.transform = `translateY(${offset})`;
    };

    const onTouchStart = (event: TouchEvent) => {
      // A second finger cancels the drag, so the sheet must spring back rather than stay where it was dragged
      if (dragging) moveTo('0px', true);
      const touch = event.touches[0];
      dragging = false;
      start = touch && event.touches.length === 1 && !isScrolledBelowTop(event.target, element) ?
        { time: Date.now(), x: touch.clientX, y: touch.clientY } :
        null;
    };

    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!start || !touch) return;

      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (!dragging) {
        if (dy < 0 || Math.abs(dx) > dy) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) >= DRAG_START_DISTANCE) start = null;
          return;
        }
        // Stop the browser starting an overscroll bounce or pull-to-refresh, which would take over the touch
        if (event.cancelable) event.preventDefault();
        if (dy < DRAG_START_DISTANCE) return;
        dragging = true;
      }

      if (event.cancelable) event.preventDefault();
      distance = Math.max(dy, 0);
      moveTo(`${distance}px`, false);
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (start && dragging) {
        const velocity = distance / Math.max(Date.now() - start.time, 1);
        if (event.type === 'touchend' && (distance > CLOSE_DISTANCE || velocity > CLOSE_VELOCITY)) {
          moveTo('100%', true);
          closeTimeout = window.setTimeout(() => onCloseRef.current(), SETTLE_DURATION);
        } else {
          moveTo('0px', true);
        }
      }
      start = null;
      dragging = false;
    };

    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd);
    element.addEventListener('touchcancel', onTouchEnd);
    return () => {
      window.clearTimeout(closeTimeout);
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [element]);
};
